import { css, SerializedStyles } from '@emotion/react';
import { Asset } from 'contentful';
import { DeviceSpec, getPicture, ImageFit } from '../../utils/helpers/image';
import { getImageFitStyles, ImageFitWrapper } from '../component-library/ImageFit';
import LazyLoadStyle from '../component-library/LazyLoadStyle';

const LandscapeImageStyles = css`
  display: none;
  @media (min-aspect-ratio: 4/3) {
    display: initial;
  }
`;

const PortraitImageStyles = css`
  @media (min-aspect-ratio: 4/3) {
    display: none;
  }
`;

function isAsset(image: unknown): image is Asset {
  return (image as Asset).fields !== undefined;
}

export type ImageProps = {
  asset?: Asset | OrientedImage;
  alt?: string;
  fit?: ImageFit;
} & ImageStyleProps &
  ({ fitWrapper?: false } | WrapperProps);

export type ImageStyleProps = {
  wrapperCss?: SerializedStyles;
  css?: SerializedStyles;
  className?: string;
  /**
   * specify what portion of the screen the image takes. For example if the image takes half of the screen, use 2
   */
  spans?: DeviceSpec;
  /**
   * specify what is the aspect ratio of the image. This is especially useful on mobile when the original image is landscape and should be rendered in portrait
   */
  aspectRatios?: DeviceSpec;
  maxWidth?: number;
};

type WrapperProps = {
  fitWrapper: true;
  deviceWidths?: DeviceSpec;
  aspectRatio?: number;
  responsiveAspectRatios?: DeviceSpec;
};

export type OrientedImage = {
  landscape?: Asset;
  portrait?: Asset;
};

const Image = (props: ImageProps) => {
  const { asset, className, alt, spans, aspectRatios, fit, maxWidth } = props;

  if (!asset) {
    return null;
  }

  const extraStyles = getStyles(props);

  const wrapperCss = css([props.wrapperCss, extraStyles.wrapperCss]);

  let image: Asset | undefined;

  if (isAsset(asset)) {
    image = asset;
  } else {
    if (asset.landscape && asset.portrait) {
      const LandscapePicture = getPicture({
        fileName: asset.landscape.fields.file.fileName,
        url: asset.landscape.fields.file.url,
        aspectRatios,
        spans,
        fit,
        maxWidth,
      });
      const PortraitPicture = getPicture({
        fileName: asset.portrait.fields.file.fileName,
        url: asset.portrait.fields.file.url,
        aspectRatios,
        spans,
        fit,
        maxWidth,
      });
      return (
        <Wrapper {...props}>
          <LazyLoadStyle wrapperCss={wrapperCss}>
            <LandscapePicture
              className={className}
              css={css([LandscapeImageStyles, extraStyles.css])}
              alt={asset.landscape.fields.description || asset.landscape.fields?.title || alt}
            />
            <PortraitPicture
              className={className}
              css={css([PortraitImageStyles, extraStyles.css])}
              alt={asset.portrait.fields?.description || asset.portrait.fields?.title || alt}
            />
          </LazyLoadStyle>
        </Wrapper>
      );
    } else {
      image = asset.landscape || asset.portrait;
    }
  }

  if (!image || !image.fields.file) {
    return null;
  }

  const Picture = getPicture({
    fileName: image.fields.file.fileName,
    url: image.fields.file.url,
    aspectRatios,
    spans,
    fit,
    maxWidth,
  });

  return (
    <Wrapper {...props}>
      <LazyLoadStyle wrapperCss={wrapperCss}>
        <Picture
          className={className}
          css={extraStyles.css}
          alt={image?.fields?.description || image?.fields?.title || alt}
        />
      </LazyLoadStyle>
    </Wrapper>
  );
};

const Wrapper = (props: ImageProps & { children: JSX.Element }) => {
  const { children } = props;
  if (props.fitWrapper) {
    const { deviceWidths, aspectRatio, responsiveAspectRatios, wrapperCss } = props;
    return (
      <ImageFitWrapper
        deviceWidths={deviceWidths}
        aspectRatio={aspectRatio}
        responsiveAspectRatios={responsiveAspectRatios}
        css={wrapperCss}
      >
        {children}
      </ImageFitWrapper>
    );
  }
  return children;
};

const getStyles = (props: ImageProps) => {
  if (props.fitWrapper) {
    return getImageFitStyles();
  }
  return { css: undefined, wrapperCss: undefined };
};

export default Image;
